<template>
  <div id="listingStatsBlurry">
    <div :class="$style.headerWrapper">
      <h2>{{ t('listingStats.title') }}</h2>
    </div>
    <div :class="$style.container">
      <div :class="$style.modal">
        <div :class="$style.modalHeaderWrapper">
          <span v-html="t('listingStats.fields.info')" />
          <span :class="$style.tenantPlusWrapper">
            <span v-html="t('listingStats.fields.tenantText')" />
            <HgBadge :class="$style.badge" theme="primarylight">
              {{ t('listingStats.fields.badge') }}
              <HgIcon :class="$style.tenantPlusIcon" icon="TenantPlus" size="x-small" />
            </HgBadge>
          </span>
        </div>
        <div :class="$style.buttonWrapper">
          <HgButton theme="secondary" @click="openTenantPlusModal()">{{
            t('listingStats.fields.cta')
          }}</HgButton>
        </div>
      </div>

      <ListingStatsBlurryItem
        v-for="ListingStatBlurryItem in listingStatsBlurry"
        :key="ListingStatBlurryItem.title"
        :class="[$style.wrapper]"
        :icon="ListingStatBlurryItem.icon"
        :title="ListingStatBlurryItem.title"
      />
    </div>
    <TenantPlusListingStatsModal
      v-if="isTenantPlusListingStatsModalLoaded"
      :isModalVisible="isTenantPlusListingStatsModalVisible"
      :entryPoint="entryPoint"
      @close="isTenantPlusListingStatsModalVisible = false"
    />
    <div :class="$style.link">
      <HgLink ref="alreadyTenantPlusCta" @click="onClickAlreadyTenantPlusCTA">
        {{ t('tenantPlus.signUpRequiredModal.alreadyTenantPlusUserCTA') }}
      </HgLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgBadge, HgButton, HgIcon, HgLink, Icon } from '@smg-real-estate/domus';
import { computed, defineAsyncComponent, ref } from 'vue';

import ListingStatsBlurryItem from '@/components/ListingStats/ListingStatsBlurryItem.vue';
import { InteractionEvent } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import type { ListingStatBlurryItem } from '@/helpers/ListingStats/types';
import { generateTenantPlusSigninUrl } from '@/helpers/TenantPlus/TenantPlusUrls';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

defineOptions({
  name: 'ListingStatsBlurry',
});

const TenantPlusListingStatsModal = defineAsyncComponent(
  () => import('@/components/TenantPlus/TenantPlusListingStatsModal.vue'),
);

interface Props {
  entryPoint: string;
}
const props = defineProps<Props>();

const isTenantPlusListingStatsModalVisible = ref<boolean>(false);
const isTenantPlusListingStatsModalLoaded = ref<boolean>(false);

const signinUrl = computed<string>(() => {
  return generateTenantPlusSigninUrl(locale.value as string);
});
const listingStatsBlurry = computed<ListingStatBlurryItem[]>(() => {
  return [
    {
      title: t('listingStats.fields.viewed') as string,
      icon: Icon.Visibility,
    },
    {
      title: t('listingStats.fields.contacted') as string,
      icon: Icon.Email,
    },
    {
      title: t('listingStats.fields.onlineSince') as string,
      icon: Icon.Calendar,
    },
  ];
});

const openTenantPlusModal = () => {
  isTenantPlusListingStatsModalVisible.value = true;
  isTenantPlusListingStatsModalLoaded.value = true;
  trackViewDemandCta();
};
const trackViewDemandCta = (): void => {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: 'pdp_interaction',
    h_event_action_007: 'openListingStatistics',
    h_event_label_008: undefined,
  });
};
const onClickAlreadyTenantPlusCTA = (): void => {
  window.location.href = signinUrl.value;
};
</script>

<style module lang="scss">
@import '@/assets/variables';

.backgroundImage {
  width: 343px;
  margin: $space-50 0;
}

.container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-bottom: $space-100;
  margin-bottom: $space-100;
  border-top: 1px solid $color-grey-50;
  border-bottom: 1px solid $color-grey-50;

  .modal {
    position: absolute;
    z-index: 2;
    max-width: 100%;
    max-height: 100%;
    padding: $space-100;
    margin-top: $space-350;
    background-color: $color-white;
    border-radius: $border-radius-l;
    box-shadow: $elevation-high;
  }

  .buttonWrapper {
    display: flex;
    justify-content: center;
    margin-top: $space-100;
  }

  .wrapper {
    position: relative;
    display: inline-flex;
    flex-direction: row;

    &:not(:last-child)::after {
      position: absolute;
      top: $space-50;
      right: 0;
      bottom: $space-50;
      z-index: 1;
      width: 1px;
      content: '';
      background-color: $color-grey-50;
    }
  }

  .tenantPlusIcon {
    margin: auto;
  }

  .modalHeaderWrapper {
    .badge {
      align-self: center;
      height: 50%;
      margin-left: $space-25;
    }
  }

  .tenantPlusWrapper {
    display: inline-block;
  }
}

@media (min-width: $viewport-sm-min) {
  .container {
    padding-bottom: 0;
  }
}
</style>
